import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { trackGwaSetupNowClicked } from '../../analytics';
import GwaEnrollModal from './GwaEnrollModal';
import GwaDashboard from './GwaDashboard';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import styles from './Gwa.module.scss';

export const GWA_PROGRAM_QUERY = gql`
  query RESCUE_gwaProgram {
    rescueGwaProgram {
      id
      slug
      promoCodeAlias
      pdpUrl
      active
      createdAt
      totalRedemptions
      monthRedemptions
      recentRedemptions {
        id
        petName
        adopterName
        breed
        age
        redeemedAt
      }
    }
  }
`;

export interface GwaRedemptionItem {
  id: string;
  petName: string | null;
  adopterName: string;
  breed: string | null;
  age: string | null;
  redeemedAt: string;
}

interface GwaProgramData {
  rescueGwaProgram: {
    id: string;
    slug: string;
    promoCodeAlias: string;
    pdpUrl: string;
    active: boolean;
    createdAt: string;
    totalRedemptions: number;
    monthRedemptions: number;
    recentRedemptions: GwaRedemptionItem[];
  } | null;
}

export default function Gwa() {
  const { data, loading, error, refetch } = useQuery<GwaProgramData>(GWA_PROGRAM_QUERY);
  const [modalDismissed, setModalDismissed] = useState(false);

  if (loading) {
    return (
      <div>
        <h1>Gift with Adoption</h1>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Gift with Adoption</h1>
        <p className={styles.errorMessage}>Something went wrong loading your GWA program. Please try refreshing.</p>
      </div>
    );
  }

  const enrollment = data?.rescueGwaProgram;

  if (enrollment) {
    return <GwaDashboard enrollment={enrollment} />;
  }

  // Modal is open by default when not enrolled, unless user explicitly dismissed it
  const modalOpen = !modalDismissed;

  return (
    <div>
      <h1>Gift with Adoption</h1>
      <EnrollEmptyState
        description={
          <>
            Not enrolled yet. Set up Gift with Adoption to offer adopters a free Fi Mini.
            <span className={styles.feeNote}>A one-time $20 activation fee applies for adopters.</span>
          </>
        }
        ctaLabel="Set up now"
        onCta={() => {
          trackGwaSetupNowClicked();
          setModalDismissed(false);
        }}
      />
      <GwaEnrollModal
        open={modalOpen}
        onClose={() => setModalDismissed(true)}
        onEnrolled={() => {
          setModalDismissed(true);
          refetch();
        }}
      />
    </div>
  );
}
